import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Building, Store, ShoppingBag, Globe, ExternalLink, AlertTriangle, ChevronDown, TrendingUp, Shield, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CUSTOMER_TYPES = [
  { id: "b2b_large", label: "Large enterprises (B2B)", icon: Building },
  { id: "b2b_small", label: "Small businesses", icon: Store },
  { id: "b2c", label: "Retail consumers (B2C)", icon: ShoppingBag },
  { id: "export", label: "Export clients", icon: Globe },
];

const IMPORTANCE_OPTIONS = [
  { value: "very_important", label: "Very important" },
  { value: "somewhat_important", label: "Somewhat important" },
  { value: "not_important", label: "Not important" },
  { value: "not_sure", label: "Not sure" },
];

export function Step4MarketPressure({ companyId, industry, onComplete, onBack }) {
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [importance, setImportance] = useState("");
  const [loading, setLoading] = useState(false);
  const [marketData, setMarketData] = useState(null);
  const [warningExpanded, setWarningExpanded] = useState(false);

  // Replace regulation abbreviations with hyperlinks
  const renderLinkedText = (text, links) => {
    if (!links || !text) return text;
    const keys = Object.keys(links).sort((a, b) => b.length - a.length);
    if (keys.length === 0) return text;
    const pattern = new RegExp(`\\b(${keys.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'g');
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      const key = match[1];
      parts.push(
        <a key={`${key}-${match.index}`} href={links[key]} target="_blank" rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary inline-flex items-center gap-0.5 font-medium">
          {key}<ExternalLink className="w-3 h-3 inline" />
        </a>
      );
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : text;
  };

  const toggleType = (typeId) => {
    setSelectedTypes((prev) =>
      prev.includes(typeId) ? prev.filter((t) => t !== typeId) : [...prev, typeId]
    );
  };

  const handleSubmit = async () => {
    if (selectedTypes.length === 0 || !importance) {
      toast.error("Please select at least one customer type and importance level.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/market/generate`, {
        company_id: companyId,
        customer_types: selectedTypes,
        sustainability_importance: importance,
      });
      setMarketData(data);
    } catch (err) {
      toast.error("Could not generate market insights.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Market context</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Who are your customers?
        </h2>
      </div>

      {!marketData ? (
        <>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-3">Select all that apply</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {CUSTOMER_TYPES.map((type) => {
                const Icon = type.icon;
                const isChecked = selectedTypes.includes(type.id);
                return (
                  <div
                    key={type.id}
                    onClick={() => toggleType(type.id)}
                    className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all duration-300 ${isChecked ? 'glass-card-selected' : ''}`}
                    data-testid={`customer-type-${type.id}`}
                  >
                    <Checkbox checked={isChecked} onCheckedChange={() => toggleType(type.id)} className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
                    <Icon className={`w-5 h-5 ${isChecked ? 'text-primary' : 'text-white/40'}`} />
                    <span className="text-white">{type.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-base text-white">How important is sustainability to your customers?</p>
            <RadioGroup value={importance} onValueChange={setImportance} className="space-y-2">
              {IMPORTANCE_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center gap-3 glass-card p-4 cursor-pointer" onClick={() => setImportance(opt.value)} data-testid={`importance-${opt.value}`}>
                  <RadioGroupItem value={opt.value} className="border-white/20 text-primary" />
                  <Label className="text-white cursor-pointer">{opt.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Button
            data-testid="market-submit-btn"
            onClick={handleSubmit}
            disabled={loading || selectedTypes.length === 0 || !importance}
            className="rounded-full px-8 h-14 bg-primary text-primary-foreground text-lg font-medium btn-glow disabled:opacity-40"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Analyzing market...
              </span>
            ) : (
              "Get market insights"
            )}
          </Button>
          {onBack && !loading && (
            <Button
              data-testid="market-back-btn"
              onClick={onBack}
              variant="ghost"
              className="rounded-full px-5 h-14 text-muted-foreground hover:text-white text-lg"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
        </>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Urgency Warning */}
          {marketData.urgency_message && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl border border-red-500/40 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(2,4,2,0.98) 100%)" }}
              data-testid="urgency-banner"
            >
              <div className="p-5 cursor-pointer" onClick={() => setWarningExpanded(!warningExpanded)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-red-300 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Your compliance window is closing</h4>
                      <p className="text-white/70 text-sm">Companies without verified sustainability data face <strong className="text-white">immediate business risks</strong>.</p>
                    </div>
                  </div>
                  <motion.div animate={{ rotate: warningExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="mt-2">
                    <ChevronDown className="w-5 h-5 text-white/40" />
                  </motion.div>
                </div>
                <div className="mt-4 pl-13 space-y-2">
                  {(marketData.risks || []).map((risk, i) => (
                    <div key={`risk-${risk.keyword}-${i}`} className="flex items-center gap-2 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>
                      <span className="text-white/80"><strong className="text-red-300">{risk.keyword}</strong> — {risk.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
              <AnimatePresence>
                {warningExpanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="border-t border-white/10">
                    <div className="p-5 space-y-5">
                      {marketData.happening_now?.length > 0 && (
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-300 font-semibold text-sm uppercase tracking-wide mb-1">This is happening now</p>
                            <div className="space-y-2">
                              {marketData.happening_now.map((item, i) => (
                                <p key={`hn-${i}-${item.text?.slice(0,20)}`} className="text-white/70 text-sm leading-relaxed">{renderLinkedText(item.text, marketData.regulation_links)}</p>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {marketData.leaders_doing && (
                        <div className="flex items-start gap-3">
                          <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-primary font-semibold text-sm uppercase tracking-wide mb-1">What leaders are doing</p>
                            <p className="text-white/70 text-sm leading-relaxed">
                              <strong className="text-white">{marketData.leaders_doing.names}</strong> already {marketData.leaders_doing.action}. They're winning contracts and trust — while laggards lose both.
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                        <div className="flex items-center gap-3">
                          <Shield className="w-5 h-5 text-primary flex-shrink-0" />
                          <p className="text-white text-sm">
                            <strong>Act now:</strong> Build your sustainability profile today.<br />
                            <span className="text-white/50">Delay:</span> <span className="text-red-300">{marketData.act_now_consequence || "Risk losing contracts, market access, and buyer trust."}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          <div className="space-y-3">
            {marketData.insights?.map((ins, i) => {
              const isObj = typeof ins === "object" && ins !== null;
              const headline = isObj ? ins.headline : null;
              const detail = isObj ? ins.detail : ins;
              const severity = isObj ? ins.severity : null;
              const category = isObj ? ins.category : null;
              const sevColors = { critical: "bg-red-500/15 text-red-300 border-red-500/25", high: "bg-amber-500/15 text-amber-300 border-amber-500/25", moderate: "bg-blue-500/15 text-blue-300 border-blue-500/25" };
              const catIcons = { regulation: Globe, buyer_pressure: ShoppingBag, market_shift: TrendingUp, competitive: ArrowRight };
              const CatIcon = (isObj && catIcons[category]) || Globe;
              return (
                <motion.div
                  key={`insight-${headline?.slice(0,15) || i}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.12 }}
                  className="glass-card p-5"
                  data-testid={`market-insight-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CatIcon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        {headline && <h4 className="text-white font-semibold text-sm">{headline}</h4>}
                        {severity && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${sevColors[severity] || sevColors.moderate}`}>
                            {severity}
                          </span>
                        )}
                      </div>
                      <p className="text-white/60 text-sm leading-relaxed">
                        {renderLinkedText(typeof detail === "string" ? detail : JSON.stringify(detail), marketData.regulation_links)}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            {onBack && (
              <Button
                data-testid="market-back-btn-results"
                onClick={onBack}
                variant="ghost"
                className="rounded-full px-5 h-14 text-muted-foreground hover:text-white text-lg"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            )}
            <Button
              data-testid="market-continue-btn"
              onClick={async () => {
                // Trigger welcome email in background
                try {
                  await axios.post(`${API}/onboarding/complete`, { company_id: companyId });
                } catch {
                  // Don't block navigation if email fails
                }
                onComplete();
              }}
              className="rounded-full px-8 h-14 bg-primary text-primary-foreground text-lg font-medium btn-glow"
            >
              View your dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default Step4MarketPressure;
